



































































































































import { Vue, Component, Ref } from 'vue-property-decorator'
import { parse } from 'papaparse'
import moment from 'moment'
import ListView from '@/components/ListView.vue'
import DateSelect from '@/components/DateSelect.vue'
import ShopAutocomplete from '@/components/ShopAutocomplete.vue'
import CSVExportBtn from '@/components/CSVExport.vue'
import { Shop } from '@/types/shop'
import { formatDateForDisplay } from '@/utils'
import { OpenDay } from '@/types'

const parseCSV = (file: File) => {
  return new Promise((resolve, reject) => {
    parse(file, {
      error: err => {
        reject(err)
      },
      complete: results => {
        return resolve(results.data)
      },
    })
  })
}

const transformCSV = (data: string[][], shops: Shop[]) => {
  const header = data[0]
  if (!header) return []
  const shopCodeIdx = header.findIndex(x => String(x).match('コード'))
  const dateIdx = header.findIndex(x => String(x).match('日'))
  const openIdx = header.findIndex(x => String(x).match('開店'))
  const closeIdx = header.findIndex(x => String(x).match('閉店'))

  const code2id: Map<string, Shop> = new Map()
  shops.forEach(shop => {
    code2id.set(shop.code, shop)
  })
  data = data.slice(1)
  return data.map(row => {
    const shop = code2id.get(row[shopCodeIdx])
    const date = moment(row[dateIdx])
    const openTime = row[openIdx] as string
    const closeTime = row[closeIdx] as string
    return {
      isValid: Boolean(shop && date.isValid() && openTime && closeTime),
      shop: shop?.id,
      shopCode: row[shopCodeIdx],
      shopName: shop?.name,
      date: date.isValid() ? date.format('YYYY-MM-DD') : '',
      openTime,
      closeTime,
    }
  })
}

@Component({
  components: { ListView, DateSelect, ShopAutocomplete, CSVExportBtn },
})
export default class OpenDayView extends Vue {
  @Ref() readonly listView!: ListView

  filterDefinition = {
    ordering: { type: String, default: '' },
    shop: { type: String, default: null },
    dateFrom: { type: String, default: null },
    dateTo: { type: String, default: null },
  }
  filter: null | Record<string, any> = null
  selected: number[] = []

  get headers() {
    return [
      { text: '', value: 'check', width: 50 },
      { text: this.$t('openDay.text.shopCode'), value: 'shopCode', width: 60 },
      { text: this.$t('openDay.text.shopName'), value: 'shopName', width: 200 },
      { text: this.$t('openDay.text.businessDate'), value: 'date', width: 100 },
      { text: this.$t('openDay.text.openTime'), value: 'openTime' },
      { text: this.$t('openDay.text.closeTime'), value: 'closeTime' },

      {
        text: this.$t('openDay.text.registeredDateTime'),
        value: 'createdAt',
        width: 150,
      },
    ]
  }
  get csvUploadHeaders() {
    return [
      { text: this.$t('openDay.text.shopCode'), value: 'shopCode' },
      { text: this.$t('openDay.text.shopName'), value: 'shopName' },
      { text: this.$t('openDay.text.shopId'), value: 'shop' },
      {
        text: this.$t('openDay.text.businessDate'),
        value: (item: OpenDay) =>
          formatDateForDisplay(item.date, this.$i18n.locale),
      },
      { text: this.$t('openDay.text.openTime'), value: 'openTime' },
      { text: this.$t('openDay.text.closeTime'), value: 'closeTime' },
    ]
  }
  shops: Shop[] = []
  bulkData: {
    isValid: boolean
    shopCode: any
    shopName: any
    shop: any
    date: string
    openTime: string
    closeTime: string
  }[] = []
  isUploading = false
  dialog = false

  get csvHeaders() {
    const t = this.$t.bind(this)
    return [
      { key: 'id', label: t('openDay.csv.id') },
      { key: 'shopCode', label: t('openDay.csv.shopCode') },
      { key: 'shopName', label: t('openDay.csv.shopName') },
      { key: 'date', label: t('openDay.csv.businessDate') },
      { key: 'openTime', label: t('openDay.csv.openTime') },
      { key: 'closeTime', label: t('openDay.csv.closeTime') },
    ]
  }
  created() {
    this.fetchShop()
  }

  updateQuery() {
    if (this.filter !== null) this.listView.updateQuery(this.filter)
    this.selected = []
  }
  async onDelete() {
    if (!confirm(String(this.$t('common.message.confirmDelete')))) return
    const api = this.$api.openDays
    try {
      await Promise.all(this.selected.map(id => api(id).delete()))
      this.selected = []
    } catch (err) {
      console.error(err)
      this.$toast.error(String(this.$t('toast.message.deleteError')))
    }
    this.listView.reload()
  }
  onSelectedChange(checked: boolean, id: number) {
    const selected = this.selected
    const idx = selected.findIndex(x => x === id)
    if (idx >= 0) {
      if (!checked) selected.splice(idx, 1)
    } else {
      if (checked) selected.push(id)
    }
  }
  async fetchShop(nextUrl?: string) {
    const { results, next } = await (nextUrl
      ? this.$api.http.get(nextUrl)
      : this.$api.shops().list())
    this.shops = this.shops.concat(results)
    if (next) this.fetchShop(next)
  }

  async onFileChanged(file: null | File) {
    console.log('onFileChanged', file)
    if (file) {
      const csvData = (await parseCSV(file)) as any[][]
      this.bulkData = transformCSV(csvData, this.shops)
    }
  }
  async upload() {
    this.isUploading = true
    try {
      await this.$api
        .openDays()
        .bulkUpload(this.bulkData.filter(x => x.isValid))
      this.bulkData = []
      this.$toast.success(String(this.$t('toast.message.uploadSuccess')))
      this.dialog = false
    } catch (err) {
      console.error(err)
      this.$toast.error(String(this.$t('toast.message.uploadFailed')))
    }
    this.isUploading = false
  }

  downloadTemplate() {
    const filename = 'template.csv'
    let rows = [
      [
        this.$t('openDay.text.shopCode'),
        this.$t('openDay.text.businessDate'),
        this.$t('openDay.text.openTime'),
        this.$t('openDay.text.closeTime'),
      ],
      ['101', '2021-05-01', '10:00', '18:00'],
    ]

    let content = rows.map(row => row.join(',')).join('\r\n')
    let bom = new Uint8Array([0xef, 0xbb, 0xbf])
    let blob = new Blob([bom, content], { type: 'text/csv' })

    if (window.navigator.msSaveBlob) {
      window.navigator.msSaveBlob(blob, filename)
    } else {
      let a = document.createElement('a')
      a.download = filename
      // a.target = '_blank'
      a.href = window.URL.createObjectURL(blob)

      // firefox対策． chromeの場合は append/remove をしなくていいらしい
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    }
  }
}
